import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert } from '@mui/material'
import { Add, MoreVert, Edit, Block } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listWarehouses, deactivateWarehouse, type WarehouseListItem } from '../../api/depozite'
import { errorMessage } from '../../api/client'
import WarehouseFormDialog from './WarehouseFormDialog'

export default function WarehousesPage() {
  const [formTarget, setFormTarget] = useState<WarehouseListItem | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: WarehouseListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses })
  const closeMenu = () => setMenuAnchor(null)

  const deactivateMutation = useMutation({
    mutationFn: deactivateWarehouse,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stocuri', 'depozite'] }),
    onError: (e) => setError(errorMessage(e)),
  })

  const columns = useMemo<GridColDef<WarehouseListItem>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 120, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    { field: 'workPointName', headerName: 'Punct de lucru', width: 180 },
    { field: 'zoneCount', headerName: 'Zone', width: 90, type: 'number' },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activ" size="small" color="success" variant="outlined" />
        : <Chip label="Inactiv" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Configurare</Typography>
        <Typography variant="inherit" color="text.primary">Depozite și locații</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h2">Depozite și locații</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă depozit</Button>
      </Box>

      <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid
          rows={data ?? []} columns={columns} loading={isLoading} density="comfortable"
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50]}
        />
      </Box>

      <WarehouseFormDialog warehouse={formTarget} onClose={() => setFormTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="deactivate" disabled={!menuAnchor.row.isActive}
            onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
            <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
